/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect } from "react";
import { Dialog, Box, Typography, Button, TextField, Stack } from "@mui/material";
import PropTypes from "prop-types";
import theme from "../../style/theme";

const LOOM_WALKTHROUGH_URL = "https://www.loom.com/share/634d96ce63e44f5f8533b25c0cdc00fa";

const PagerDutySetupDialog = ({
  open,
  onClose,
  serviceName = "",
  serviceUrl = "",
  initialIntegrationKey = "",
  onSave,
  isSaving = false,
}) => {
  const [integrationKey, setIntegrationKey] = useState(initialIntegrationKey);
  const [keyError, setKeyError] = useState(false);

  useEffect(() => {
    if (open) {
      setIntegrationKey(initialIntegrationKey);
      setKeyError(false);
    }
  }, [open, initialIntegrationKey]);

  const handleSave = () => {
    if (!integrationKey || !integrationKey.trim()) {
      setKeyError(true);
      return;
    }
    onSave(integrationKey.trim());
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box sx={{ p: 3, width: 520 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          PagerDuty Service Integration
        </Typography>
        <Stack spacing={2}>
          <TextField label="Name" value={serviceName} size="small" fullWidth disabled />
          <TextField
            label="Integration Key"
            placeholder="Enter Integration Key"
            value={integrationKey}
            onChange={(e) => {
              setIntegrationKey(e.target.value);
              if (keyError) setKeyError(false);
            }}
            error={keyError}
            helperText={
              <span>
                Get the Integration Key from {""}
                <a
                  href={serviceUrl}
                  style={{ color: theme.palette.primary.main }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <p
                    style={{
                      textDecoration: "underline",
                      display: "inline",
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    PagerDuty Settings
                  </p>
                </a>{" "}
                &gt; Integrations &gt; Events API v2.
                <br />
                <br />
                If it doesn&apos;t exist yet, you may need to create it {""}
                <a
                  href={LOOM_WALKTHROUGH_URL}
                  style={{ color: theme.palette.primary.main }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <p
                    style={{
                      textDecoration: "underline",
                      display: "inline",
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    (here&apos;s how)
                  </p>
                </a>{" "}
              </span>
            }
            size="small"
            fullWidth
          />
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: "flex-end" }}>
          <Button variant="outlined" color="textIcon" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

PagerDutySetupDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  serviceName: PropTypes.string,
  serviceUrl: PropTypes.string,
  initialIntegrationKey: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default PagerDutySetupDialog;
